import { fetchApiGetAiChatList } from '@repo/api-codegen';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { TimeRangeWithAll } from '../types';

export const useInfiniteAiChatList = ({
	timeRange,
	sortOrder,
	limit = 10,
}: {
	timeRange: TimeRangeWithAll;
	sortOrder: 'newest' | 'oldest';
	limit?: number;
}) => {
	const query = useInfiniteQuery({
		queryKey: ['ai-chat-list-infinite', timeRange, sortOrder, limit],
		queryFn: async ({ pageParam = 0, signal }) => {
			const response = await fetchApiGetAiChatList(
				{
					queryParams: {
						time_range: timeRange,
						sort_order: sortOrder,
						limit,
						offset: pageParam,
					},
				},
				signal
			);
			return response;
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			if (!lastPage.has_next_page) {
				return undefined;
			}
			const nextOffset = allPages.reduce(
				(acc, page) => acc + (page.items?.length ?? 0),
				0
			);
			return nextOffset;
		},
		refetchOnWindowFocus: false,
	});

	const allItems = useMemo(() => {
		const items = query.data?.pages.flatMap((page) => page.items) ?? [];
		return items;
	}, [query.data?.pages]);

	const totalCount = query.data?.pages[0]?.total_count ?? 0;

	return {
		...query,
		allItems,
		totalCount,
	};
};
